import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MessageCircle, ChevronRight, ArrowLeft, MapPin, Gauge, Clock, Users, Fuel, Cog } from "lucide-react";
import { api } from "@/lib/api";
import { useLang, pick } from "@/contexts/LanguageContext";
import { openWhatsApp, websitePhoneLabel } from "@/lib/whatsapp";
import { carDetailPath, pathFor, ROUTE_KEYS } from "@/i18n/routeMap";
import SeoHead from "@/components/SeoHead";
import ImageModal from "@/components/cars/ImageModal";
import BookingModal from "@/components/cars/BookingModal";
import ConditionsDrawer from "@/components/cars/ConditionsDrawer";
import {
  PUBLIC_DOMAIN,
  buildLocalBusinessJsonLd,
  buildVehicleJsonLd,
  buildBreadcrumb,
  buildFaqJsonLd,
  CATEGORY_COPY,
  defaultSeoTitle,
  defaultSeoDescription,
  altForVehicleImage,
} from "@/lib/seo";
import { localizeVehicleFact } from "@/lib/vehicleFacts";

export default function VehicleDetail() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [settings, setSettings] = useState({});
  const [error, setError] = useState(false);
  const [gallery, setGallery] = useState(false);
  const [conditions, setConditions] = useState(false);
  const [booking, setBooking] = useState(false);
  const [galleryStart, setGalleryStart] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [v, s] = await Promise.all([
          api.get(`/vehicles/by-slug/${slug}`),
          api.get("/settings"),
        ]);
        if (!alive) return;
        setVehicle(v.data);
        setSettings(s.data || {});
      } catch {
        if (alive) setError(true);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const sorted = useMemo(() => {
    if (!vehicle?.images?.length) return [];
    return [...vehicle.images]
      .map((i) => (typeof i === "string" ? { url: i, isMain: false, order: 0 } : i))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [vehicle]);
  const mainImage = sorted.find((i) => i.isMain) || sorted[0];
  const mainUrl = mainImage?.url || "";
  const allUrls = sorted.map((i) => i.url).filter(Boolean);

  if (error) {
    return (
      <div className="bg-[#0C0C0C] pt-40 pb-24 min-h-screen text-center text-[#F5F1EB]" data-testid="vehicle-detail-404">
        <h1 className="font-display text-3xl mb-4">404</h1>
        <p className="text-[#8A8A8A]">{t.vehicleDetail.notFound}</p>
        <Link to={pathFor(ROUTE_KEYS.CATALOG, lang)} className="btn-gold mt-8 inline-flex">
          <ArrowLeft size={14} /> {t.vehicleDetail.backToCatalog}
        </Link>
      </div>
    );
  }
  if (!vehicle) {
    return <div className="bg-[#0C0C0C] pt-40 pb-24 min-h-screen text-center text-[#8A8A8A]" data-testid="vehicle-detail-loading">Loading…</div>;
  }

  const title = pick(vehicle.seoTitle, lang) || defaultSeoTitle(vehicle, lang);
  const desc = pick(vehicle.seoDescription, lang) || defaultSeoDescription(vehicle, lang);
  const longDesc = pick(vehicle.seoDescriptionLong, lang) || pick(vehicle.descriptionFull, lang) || pick(vehicle.descriptionShort, lang);
  const catLabel = pick(vehicle.categoryLabel, lang) || vehicle.categoryKey;
  const categoryCopy = CATEGORY_COPY[vehicle.categoryKey]?.[lang];
  const alt = pick(vehicle.images?.[0]?.alt, lang) || altForVehicleImage(vehicle.name, lang);

  const breadcrumb = [
    { name: t.nav.catalog, path: pathFor(ROUTE_KEYS.CATALOG, lang) },
    { name: vehicle.name, path: carDetailPath(vehicle.slug, lang) },
  ];

  const jsonLd = [
    buildLocalBusinessJsonLd(settings),
    buildVehicleJsonLd(vehicle, lang),
    buildBreadcrumb(breadcrumb),
    buildFaqJsonLd(vehicle.faqs || [], lang),
  ];

  const openGallery = (idx = 0) => {
    setGalleryStart(idx);
    setGallery(true);
  };

  const handleWaClick = () => {
    const msg = t.whatsapp.simple(vehicle.name);
    openWhatsApp(msg, settings, lang, { vehicleId: vehicle.id, vehicleName: vehicle.name });
  };

  return (
    <div className="bg-[#0C0C0C] min-h-screen" data-testid={`vehicle-detail-${vehicle.slug}`}>
      <SeoHead
        title={title}
        description={desc}
        image={mainUrl}
        lang={lang}
        pageKey={ROUTE_KEYS.CAR_DETAIL}
        slug={vehicle.slug}
        noindex={vehicle.noindex || !vehicle.available || vehicle.seoEnabled === false}
        readyLocales={Object.entries(vehicle.contentStatus || {}).filter(([, status]) => status === "READY").map(([locale]) => locale)}
        jsonLd={jsonLd}
      />

      {/* Breadcrumbs */}
      <div className="pt-28 md:pt-32 px-6 md:px-10 max-w-6xl mx-auto">
        <nav className="text-[11px] tracking-[0.18em] uppercase text-[#8A8A8A] flex items-center gap-2" data-testid="vd-breadcrumb">
          <Link to={pathFor(ROUTE_KEYS.HOME, lang)} className="hover:text-[#C9A55A]">{t.nav.home || "Home"}</Link>
          <ChevronRight size={12} />
          <Link to={pathFor(ROUTE_KEYS.CATALOG, lang)} className="hover:text-[#C9A55A]">{t.nav.catalog}</Link>
          <ChevronRight size={12} />
          <span className="text-[#C9A55A]">{vehicle.name}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="px-6 md:px-10 max-w-6xl mx-auto mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          <button
            onClick={() => openGallery(0)}
            className="lg:col-span-3 aspect-[4/3] block bg-[#2A2A2A] border border-[#2A2A2A] hover:border-[#C9A55A] transition-colors overflow-hidden"
            data-testid="vd-hero-image">
            {mainUrl && <img src={mainUrl} alt={alt} className="w-full h-full object-cover"/>}
          </button>

          <div className="lg:col-span-2 lg:pt-2" data-testid="vd-summary">
            <div className="text-overline text-[#C9A55A]">{catLabel}</div>
            <h1 className="font-display text-[#F5F1EB] text-3xl md:text-5xl mt-3 leading-tight">{vehicle.name}</h1>

            {vehicle.location && (
              <div className="flex items-center gap-1.5 text-[11px] text-[#C9A55A] uppercase tracking-[0.14em] mt-3">
                <MapPin size={12}/> {vehicle.location}
              </div>
            )}

            {vehicle.priceMonthly > 0 && (
              <div className="mt-6">
                <div className="text-[10px] tracking-[0.18em] uppercase text-[#8A8A8A]">{t.catalog.from}</div>
                <div className="font-display text-[#C9A55A] text-4xl">€{Math.round(vehicle.priceMonthly)}<span className="text-base ml-2 text-[#8A8A8A]">{t.catalog.perMonth}</span></div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mt-6 text-xs text-[#F5F1EB]" data-testid="vd-specs">
              {vehicle.horsepower ? <Spec icon={Gauge} label={`${vehicle.horsepower} ${t.catalog.hp}`}/> : null}
              {vehicle.topSpeed ? <Spec icon={Gauge} label={`${vehicle.topSpeed} km/h`}/> : null}
              {vehicle.acceleration ? <Spec icon={Clock} label={`${vehicle.acceleration}s 0-100`}/> : null}
              {vehicle.transmission ? <Spec icon={Cog} label={localizeVehicleFact(vehicle.transmission, lang)}/> : null}
              {vehicle.fuelType ? <Spec icon={Fuel} label={localizeVehicleFact(vehicle.fuelType, lang)}/> : null}
              {vehicle.seats ? <Spec icon={Users} label={`${vehicle.seats} ${t.catalog.seats}`}/> : null}
            </div>

            <button onClick={handleWaClick} className="btn-red w-full mt-6" data-testid="vd-wa-btn">
              <MessageCircle size={16}/> {t.catalog.reserve}
            </button>
            <button onClick={() => setBooking(true)} className="w-full mt-2 border border-[#2A2A2A] hover:border-[#C9A55A] hover:text-[#C9A55A] text-[#F5F1EB] text-xs tracking-[0.16em] uppercase font-semibold py-3"
              data-testid="vd-reserve-with-dates">
              {t.vehicleDetail.reserveWithDates}
            </button>
            <button onClick={() => setConditions(true)} className="w-full mt-2 text-xs tracking-[0.16em] uppercase text-[#8A8A8A] hover:text-[#C9A55A] py-2"
              data-testid="vd-open-conditions">
              {t.catalog.conditions}
            </button>

            <div className="mt-6 text-[10px] tracking-[0.18em] uppercase text-[#8A8A8A]">
              {t.vehicleDetail.contactDirect}: <span className="text-[#F5F1EB]">{websitePhoneLabel(settings)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery thumbs */}
      {allUrls.length > 1 && (
        <section className="px-6 md:px-10 max-w-6xl mx-auto mt-10" data-testid="vd-gallery">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {allUrls.slice(0, 6).map((url, i) => (
              <button key={i} onClick={() => openGallery(i)} className="aspect-[4/3] bg-[#2A2A2A] overflow-hidden hover:opacity-90">
                <img src={url} alt={`${alt} ${i + 1}`} className="w-full h-full object-cover" loading="lazy"/>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Description */}
      <section className="px-6 md:px-10 max-w-3xl mx-auto mt-16 text-[#F5F1EB]/85 font-light leading-relaxed text-[15px] space-y-4" data-testid="vd-description">
        {longDesc && <p>{longDesc}</p>}
        {categoryCopy && <p className="text-[#F5F1EB]/65">{categoryCopy}</p>}
      </section>

      {/* Conditions strip */}
      <section className="px-6 md:px-10 max-w-3xl mx-auto mt-10" data-testid="vd-conditions-strip">
        <div className="border border-[#2A2A2A] p-5 grid grid-cols-2 gap-4 text-sm">
          {vehicle.deposit > 0 && (
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[#8A8A8A]">{t.vehicleDetail.deposit}</div>
              <div className="text-[#F5F1EB] mt-1">€{Math.round(vehicle.deposit)}</div>
            </div>
          )}
          {/* Prompt 15 §5 — mileage figures never rendered publicly. Terms
              are disclosed to the client before the booking is confirmed. */}
        </div>
      </section>

      {/* FAQ */}
      {(vehicle.faqs || []).filter((f) => pick(f.q, lang) && pick(f.a, lang)).length > 0 && (
        <section className="px-6 md:px-10 max-w-3xl mx-auto mt-16" data-testid="vd-faq">
          <h2 className="font-display text-[#F5F1EB] text-2xl mb-6">{t.vehicleDetail.faqTitle}</h2>
          <div className="space-y-4">
            {vehicle.faqs.map((f, i) => {
              const q = pick(f.q, lang), a = pick(f.a, lang);
              if (!q || !a) return null;
              return (
                <details key={i} className="border border-[#2A2A2A] p-4 group" data-testid={`vd-faq-${i}`}>
                  <summary className="cursor-pointer text-[#C9A55A] text-sm tracking-wide font-semibold list-none flex items-center justify-between">
                    <span>{q}</span>
                    <ChevronRight size={14} className="transition-transform group-open:rotate-90"/>
                  </summary>
                  <p className="mt-3 text-[#F5F1EB]/80 text-sm font-light leading-relaxed">{a}</p>
                </details>
              );
            })}
          </div>
        </section>
      )}

      {/* CTA back */}
      <section className="px-6 md:px-10 max-w-3xl mx-auto mt-16 pb-24 text-center">
        <Link
          to={pathFor(ROUTE_KEYS.CATALOG, lang)}
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-[#C9A55A] hover:text-[#F5F1EB]"
          data-testid="vd-back-link">
          <ArrowLeft size={12}/> {t.vehicleDetail.backToCatalog}
        </Link>
      </section>

      <ImageModal open={gallery} onClose={() => setGallery(false)} images={allUrls.length ? allUrls : [mainUrl]} title={vehicle.name} startIndex={galleryStart}/>
      <ConditionsDrawer open={conditions} onClose={() => setConditions(false)} vehicle={vehicle} settings={settings}/>
      <BookingModal open={booking} onClose={() => setBooking(false)} vehicle={vehicle} settings={settings}/>
    </div>
  );
}

function Spec({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 border border-[#2A2A2A] px-3 py-2">
      <Icon className="text-[#C9A55A]" size={14}/>
      <span>{label}</span>
    </div>
  );
}

// Ensure pretty SSR-like canonical works for sharing
void PUBLIC_DOMAIN;
// Ensure navigate import isn't unused (reserved for future redirects)
void useNavigate;
